// [PHASE 3.1.8 - NOV 11, 2025]: Initialize Sources System with all available sourcebooks
// [WHY]: Added SRD_FEATS and SRD_BACKGROUNDS imports for complete SRD migration
// [CRITICAL-5 - NOV 15, 2025]: Added SRD_SUBCLASSES for 40 SRD subclasses migration
import { sourcesManager, migrateExistingData } from '../sourcesManager';
import { BASE_ITEMS } from '../gameData';
import { SRD_SPELLS } from '../srdSpells';
import { SRD_FEATS } from '../srdFeats'; // [PHASE 3.1.8]: Added for 62 SRD feats
import { SRD_BACKGROUNDS, SRD_SUBCLASSES } from '../srdCharacterData'; // [PHASE 3.1.8]: Added for 13 backgrounds + [CRITICAL-5]: 40 subclasses
import { TASHAS_SOURCE } from './tashas';
import { XANATHARS_SOURCE } from './xanathars';
import { FIZBANS_SOURCE } from './fizbans';
import { VOLOS_SOURCE } from './volos'; // [CONTENT CREATORS - FEB 2026]: Volo's Guide with Aasimar race

// Initialize the sources system with all content
export function initializeSourcesSystem() {
  // [PHASE 3.1.8 - NOV 11, 2025]: Migrate existing SRD content (items, spells, feats, backgrounds)
  // [CRITICAL-5 - NOV 15, 2025]: Added SRD_SUBCLASSES (40 SRD subclasses)
  migrateExistingData(BASE_ITEMS, SRD_SPELLS, SRD_FEATS, SRD_BACKGROUNDS, SRD_SUBCLASSES);

  // Check if additional sources have already been imported
  const allSources = sourcesManager.getAllSources();

  // Import Tasha's if not already present
  if (!allSources.tashas) {
    try {
      sourcesManager.importSource(TASHAS_SOURCE);
    } catch (e) {
      console.error("Failed to import Tasha's:", e);
    }
  }

  // Import Xanathar's if not already present
  if (!allSources.xanathars) {
    try {
      sourcesManager.importSource(XANATHARS_SOURCE);
    } catch (e) {
      console.error("Failed to import Xanathar's:", e);
    }
  }

  // Import Fizban's if not already present
  if (!allSources.fizbans) {
    try {
      sourcesManager.importSource(FIZBANS_SOURCE);
    } catch (e) {
      console.error("Failed to import Fizban's:", e);
    }
  }

  // [CONTENT CREATORS - FEB 2026]: Import Volo's Guide to Monsters
  // [PURPOSE]: Training example — Aasimar race with 3 subraces proves Race Creator system works
  if (!allSources.volos) {
    try {
      sourcesManager.importSource(VOLOS_SOURCE);
    } catch (e) {
      console.error("Failed to import Volo's:", e);
    }
  }

  return sourcesManager;
}

// [PHASE B / #019 — B2-5]: BASE_ITEM_CATEGORY_IDS is the canonical list
//   of BASE_ITEMS storage buckets. Pre-fix this template was hardcoded
//   with 9 entries including 4 phantoms (treasure / scrolls / wondrous /
//   potions) that no SRD or source item ever populated, and was missing
//   real BASE_ITEMS keys (magical_items, art_objects, gems, etc.) so
//   items in those categories had to fall through to the dynamic
//   bucket-creation branch below.
// eslint-disable-next-line import/first -- deliberately co-located with the comment above (ES imports hoist; no behavior change). DEV-OVERLAY-ESLINT.
import { BASE_ITEM_CATEGORY_IDS } from '../../constants/baseItemCategories';

// Get items from enabled sources, formatted for the app's existing structure
export function getItemsFromSources() {
  const items = sourcesManager.getAllItems();
  const formattedItems = Object.fromEntries(
    BASE_ITEM_CATEGORY_IDS.map((id) => [id, {}])
  );

  for (const [key, item] of Object.entries(items)) {
    const category = item.category || 'items';
    if (!formattedItems[category]) {
      formattedItems[category] = {};
    }

    // Extract the item key (remove category prefix if present)
    // [FIX JAN 2026]: Also strip 'items_' prefix as fallback for items with mismatched key prefixes
    // [WHY]: Keys like 'items_unbreakable_arrow' with category 'ammunition' weren't being stripped
    // [RESULT]: Was showing "Items Unbreakable Arrow" instead of "Unbreakable Arrow"
    let itemKey = key.replace(`${category}_`, '');
    // If category prefix didn't match, try removing 'items_' prefix
    if (itemKey === key && key.startsWith('items_')) {
      itemKey = key.replace('items_', '');
    }
    formattedItems[category][itemKey] = item;
  }

  return formattedItems;
}

// Get spells from enabled sources, formatted for the app's existing structure
export function getSpellsFromSources() {
  const spells = sourcesManager.getAllSpells();
  const formattedSpells = {};

  // Group spells by level
  for (let level = 0; level <= 9; level++) {
    formattedSpells[level] = [];
  }

  for (const spell of Object.values(spells)) {
    const level = spell.level || 0;
    if (formattedSpells[level]) {
      formattedSpells[level].push(spell);
    }
  }

  return formattedSpells;
}

// Get feats from enabled sources
export function getFeatsFromSources() {
  return sourcesManager.getAllFeats();
}

// Get abilities from enabled sources
export function getAbilitiesFromSources() {
  return sourcesManager.getAllAbilities();
}

// Get backgrounds from enabled sources
export function getBackgroundsFromSources() {
  return sourcesManager.getAllBackgrounds();
}

// Get boons from enabled sources
export function getBoonsFromSources() {
  return sourcesManager.getAllBoons();
}

// [CRITICAL-5 - NOV 15, 2025]: Get all subclasses from enabled sources
// [PURPOSE]: Allow LevelUpWizard and CharacterCreationWizard to query subclasses
export function getSubclassesFromSources() {
  return sourcesManager.getAllSubclasses();
}

// [CRITICAL-5 - NOV 15, 2025]: Get subclasses for a specific class
// [PURPOSE]: Filter subclasses by class name for level-up wizard
// [RETURNS]: Object containing only subclasses for the specified class
export function getSubclassesForClassFromSources(className) {
  return sourcesManager.getSubclassesForClass(className);
}

// Search for content across all enabled sources
export function searchSourceContent(query, category = null) {
  return sourcesManager.searchContent(query, category);
}

// Check if a specific item exists in enabled sources
export function itemExistsInSources(category, itemKey) {
  return sourcesManager.contentExists('items', `${category}_${itemKey}`);
}

// [PHASE B / #067 — 2026-05-07]: Targeted single-item lookup. Closes the
//   v1.0.1 §1.11 SearchableItemSelector emission audit. Pre-#067 the
//   InventoryItem constructor only consulted `BASE_ITEMS[category][baseType]`
//   — when the picker advertised a `(category, itemKey)` pair from an enabled
//   source (Tasha's, Xanathar's, custom CW content), the lookup missed and the
//   constructor fell through to a `{}` stub. Defensive guards (Batch 15 + 17)
//   prevented the crash but the resolved item's data (description, size,
//   value, charges) was silently dropped. This helper is the targeted lookup
//   the constructor needs as a fallback. Returns the stored item object or
//   null when the pair truly doesn't exist in any enabled source.
export function findItemInSources(category, itemKey) {
  if (!category || !itemKey) return null;
  // sourcesManager stores items keyed `{category}_{itemKey}`.
  const allItems = sourcesManager.getAllItems();
  return allItems[`${category}_${itemKey}`] || null;
}

// Check if a specific spell exists in enabled sources
export function spellExistsInSources(spellKey) {
  return sourcesManager.contentExists('spells', spellKey);
}

// Add item to a custom source
export function addItemToSource(sourceId, item) {
  const category = item.category || 'items';
  const key = `${category}_${item.baseType || item.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
  return sourcesManager.addContentToSource(sourceId, 'items', key, item);
}

// Add spell to a custom source
export function addSpellToSource(sourceId, spell) {
  const key = spell.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
  return sourcesManager.addContentToSource(sourceId, 'spells', key, spell);
}

// Add feat to a custom source
export function addFeatToSource(sourceId, feat) {
  const key = feat.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
  return sourcesManager.addContentToSource(sourceId, 'feats', key, feat);
}

// Remove item from a custom source
export function removeItemFromSource(sourceId, itemKey) {
  return sourcesManager.removeContentFromSource(sourceId, 'items', itemKey);
}

// Remove spell from a custom source
export function removeSpellFromSource(sourceId, spellKey) {
  return sourcesManager.removeContentFromSource(sourceId, 'spells', spellKey);
}

// Remove feat from a custom source
export function removeFeatFromSource(sourceId, featKey) {
  return sourcesManager.removeContentFromSource(sourceId, 'feats', featKey);
}

// [CRITICAL-5 - NOV 15, 2025]: Add subclass to a custom source
// [PURPOSE]: Allow custom homebrew subclasses to be added to sources
export function addSubclassToSource(sourceId, subclass) {
  const key = subclass.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
  return sourcesManager.addContentToSource(sourceId, 'subclasses', key, subclass);
}

// [CRITICAL-5 - NOV 15, 2025]: Remove subclass from a custom source
export function removeSubclassFromSource(sourceId, subclassKey) {
  return sourcesManager.removeContentFromSource(sourceId, 'subclasses', subclassKey);
}

// [CONTENT CREATORS - FEB 2026]: Get all races from enabled sources
// [PURPOSE]: Allow CharacterCreationWizard to merge custom races with SRD_RACES
export function getRacesFromSources() {
  return sourcesManager.getAllRaces();
}

// [CONTENT CREATORS - FEB 2026]: Get all classes from enabled sources
// [PURPOSE]: Allow CharacterCreationWizard to merge custom classes with SRD_CLASSES
export function getClassesFromSources() {
  return sourcesManager.getAllClasses();
}

// [CONTENT CREATORS - FEB 2026]: Add custom race to a source
export function addRaceToSource(sourceId, race) {
  const key = race.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
  return sourcesManager.addContentToSource(sourceId, 'races', key, race);
}

// [CONTENT CREATORS - FEB 2026]: Add custom class to a source
export function addClassToSource(sourceId, classData) {
  const key = classData.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
  return sourcesManager.addContentToSource(sourceId, 'classes', key, classData);
}

// [CONTENT CREATORS - FEB 2026]: Remove race from a source
export function removeRaceFromSource(sourceId, raceKey) {
  return sourcesManager.removeContentFromSource(sourceId, 'races', raceKey);
}

// [CONTENT CREATORS - FEB 2026]: Remove class from a source
export function removeClassFromSource(sourceId, classKey) {
  return sourcesManager.removeContentFromSource(sourceId, 'classes', classKey);
}

// [CW-TRIO-SOURCES 2026-07-12]: Workshop Load-drawer listing — user-authored
// content only (enabled, non-builtin sources). Each entry carries its address
// ({_source, _key}) so edit-mode can re-target the owning source and delete
// can remove in place via removeContentFromSource.
export function listUserSourceContent(category) {
  return Object.values(sourcesManager.getAllSources())
    .filter((source) => source.enabled && !source.builtin)
    .flatMap((source) =>
      Object.entries((source.content && source.content[category]) || {}).map(([key, data]) => ({
        ...data,
        id: data.id || key,
        name: data.name || key,
        _key: key,
        _source: source.id,
      }))
    );
}

// Update content in a source
export function updateContentInSource(sourceId, category, contentKey, updatedData) {
  return sourcesManager.addContentToSource(sourceId, category, contentKey, updatedData);
}

// Export the sources manager for direct access if needed
export { sourcesManager };